import { useState, ChangeEvent, FormEvent } from "react";
import axios, { AxiosError } from "axios";
import { RedactionType } from "../types/shared.types";
import { ToleranceSettings } from "../components/common/TrustToleranceSettings";
import { redactionModels } from "../utils/helpers";
import { RedactionResult } from "../types/redaction.types";

export const useRedactionTool = (
  onRedactionComplete: (result: RedactionResult) => void
) => {
  const [inputFile, setInputFile] = useState<File | null>(null);
  const [modelChoice, setModelChoice] = useState<string>(
    "gliner-community/gliner_small-v2.5"
  );
  const [redactionType, setRedactionType] =
    useState<RedactionType>("names_only");
  const [toleranceSettings, setToleranceSettings] = useState<ToleranceSettings>(
    {
      trustLevel: "medium",
      confidenceThreshold: 75,
    }
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setInputFile(e.target.files[0]);
    }
  };

  const handleModelChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setModelChoice(event.target.value);
  };

  const handleRedactionTypeChange = (redactionType: RedactionType) => {
    setRedactionType(redactionType);
  };

  const handleToleranceSettingsChange = (
    toleranceSettings: ToleranceSettings
  ) => {
    setToleranceSettings(toleranceSettings);
  };

  const handleRedactClick = async (event?: FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault(); // Prevent default form submission if called from form
    }

    if (!inputFile) {
      setError("Please select a CSV or XLSX file to process.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    const formData = new FormData();
    formData.append("file_to_process", inputFile);
    formData.append("model_choice", modelChoice);
    formData.append("redaction_type", redactionType);

    // Add tolerance settings if available
    console.log("Tolerance settings being sent:", toleranceSettings);
    if (toleranceSettings) {
      formData.append("tolerance_settings", JSON.stringify(toleranceSettings));
      console.log(
        "Tolerance settings JSON:",
        JSON.stringify(toleranceSettings)
      );
    } else {
      console.log("WARNING: No tolerance settings available!");
    }

    try {
      // Use local backend for development, production API for production
      const apiUrl =
        process.env.NODE_ENV === "production"
          ? "https://ner-api.pobl.tech/api/redact"
          : "http://localhost:8000/api/redact";

      const response = await axios.post(apiUrl, formData, {
        responseType: "json",
      });

      const responseData = response.data as RedactionResult;

      // Create a blob from the file content
      let blob;
      // Check if the content is base64 encoded or plain JSON (for Excel)
      if (responseData.mimetype.includes("json")) {
        // For excel, the content might be JSON, not a base64 string
        blob = new Blob([JSON.stringify(responseData.file_content)], {
          type: responseData.mimetype,
        });
      } else {
        // For other files, decode the base64 string
        const byteCharacters = atob(responseData.file_content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: responseData.mimetype });
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", responseData.filename); // Use filename from response
      document.body.appendChild(link);
      link.click();

      // Clean up
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);

      setSuccessMessage(
        `Successfully processed file. Downloaded as ${responseData.filename}.`
      );
      setIsLoading(false);

      // Pass the full result up to the parent component (Dashboard)
      onRedactionComplete(responseData);
    } catch (err) {
      console.error("Redaction error:", err);
      let errorMessage =
        "An unexpected error occurred. Please check the console for details.";

      // Updated error handling for JSON response
      if (axios.isAxiosError(err)) {
        const axiosError = err as AxiosError;
        if (
          axiosError.response?.data &&
          typeof axiosError.response.data === "object"
        ) {
          const errorData = axiosError.response.data as { error?: string };
          errorMessage = `Error: ${
            errorData.error || JSON.stringify(axiosError.response.data)
          }`;
        } else if (axiosError.message) {
          errorMessage = `Error: ${axiosError.message}`;
        }
      } else if (err instanceof Error) {
        errorMessage = `Error: ${err.message}`;
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return {
    inputFile,
    modelChoice,
    redactionType,
    toleranceSettings,
    isLoading,
    error,
    successMessage,
    handleFileChange,
    handleModelChange,
    handleRedactionTypeChange,
    handleToleranceSettingsChange,
    handleRedactClick,
  };
};
